import { FeedbackType } from "@sp-crm/core";
import { InlineFeedbackResponse } from "components/feedback/inline-feedback";
import { useCallback, useState } from "react";
import { AiSummaryShape, AiSummaryState } from "./ai-summary-state";

export interface EntityAiSummaryQueries {
    summary: AiSummaryShape | null | undefined;
    isQueryLoading: boolean;
    queryError: string | undefined;
    refetchSummary: () => void;

    triggerGenerate: () => Promise<unknown>;
    isGenerating: boolean;
    generateError: string | undefined;

    triggerSubmitFeedback: (input: {
        summaryId: string;
        feedbackType: FeedbackType;
        feedbackText?: string;
    }) => void;
    isSubmittingFeedback: boolean;
    submitFeedbackError: string | undefined;

    triggerUpdateText: (input: { summaryId: string; text: string }) => Promise<unknown>;
    updateTextError: string | undefined;

    triggerRemoveSummary: (input: { summaryId: string }) => Promise<unknown>;
    isRemoving: boolean;
    removeSummaryError: string | undefined;

    trackGenerate: () => void;
    trackRegenerate: () => void;
}

export const useEntityAiSummary = (deps: EntityAiSummaryQueries): AiSummaryState => {
    const {
        summary: rawSummary,
        isQueryLoading,
        queryError,
        refetchSummary,
        triggerGenerate,
        isGenerating,
        generateError,
        triggerSubmitFeedback,
        isSubmittingFeedback,
        submitFeedbackError,
        triggerUpdateText,
        updateTextError,
        triggerRemoveSummary,
        isRemoving,
        removeSummaryError,
        trackGenerate,
        trackRegenerate,
    } = deps;

    const summary = rawSummary ?? null;
    const [feedbackGiven, setFeedbackGiven] = useState<InlineFeedbackResponse>(null);
    const [showRemoveButton, setShowRemoveButton] = useState(false);

    const updateText = useCallback(
        async (newText: string): Promise<boolean> => {
            if (!summary) return false;
            try {
                await triggerUpdateText({ summaryId: summary.id, text: newText });
                refetchSummary();
                return true;
            } catch {
                return false;
            }
        },
        [summary, triggerUpdateText, refetchSummary],
    );

    const generate = useCallback(() => {
        if (summary) {
            trackRegenerate();
        } else {
            trackGenerate();
        }
        triggerGenerate()
            .then(() => {
                setFeedbackGiven(null);
                setShowRemoveButton(false);
                refetchSummary();
            })
            .catch((): void => undefined);
    }, [summary, triggerGenerate, refetchSummary, trackGenerate, trackRegenerate]);

    const submitPositiveFeedback = useCallback(() => {
        if (!summary) return;
        setFeedbackGiven("positive");
        triggerSubmitFeedback({
            summaryId: summary.id,
            feedbackType: FeedbackType.Positive,
        });
    }, [summary, triggerSubmitFeedback]);

    const startNegativeFeedback = useCallback(() => {
        if (!summary) return;
        setFeedbackGiven("negative");
        setShowRemoveButton(true);
    }, [summary]);

    const submitNegativeFeedback = useCallback(
        (combinedText: string | null) => {
            if (!summary) return;
            triggerSubmitFeedback({
                summaryId: summary.id,
                feedbackType: FeedbackType.Negative,
                feedbackText: combinedText || undefined,
            });
        },
        [summary, triggerSubmitFeedback],
    );

    const removeSummary = useCallback(() => {
        if (!summary) return;
        triggerRemoveSummary({ summaryId: summary.id })
            .then(() => {
                setFeedbackGiven(null);
                setShowRemoveButton(false);
                refetchSummary();
            })
            .catch((): void => undefined);
    }, [summary, triggerRemoveSummary, refetchSummary]);

    return {
        summary,
        isLoading: isQueryLoading || isGenerating,
        isQueryLoading,
        isGenerating,
        isRemoving,
        isSubmittingFeedback,
        error:
            queryError ||
            generateError ||
            updateTextError ||
            submitFeedbackError ||
            removeSummaryError,
        feedbackGiven,
        showRemoveButton,
        generate,
        updateText,
        submitPositiveFeedback,
        startNegativeFeedback,
        submitNegativeFeedback,
        removeSummary,
    };
};
